import React, { useEffect, useState } from 'react';
import { isStandalone } from '../utils/pwaUpdate';

interface UpdatePayload {
  version?: string;
  redirectTo?: string;
  message?: string;
  releaseNotes?: string[];
}

const UpdateBanner: React.FC = () => {
  const [payload, setPayload] = useState<UpdatePayload | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!isStandalone()) return; // Only show in PWA standalone mode

    // Initialize from localStorage if present
    try {
      const raw = localStorage.getItem('app_update_payload');
      if (raw) setPayload(JSON.parse(raw));
    } catch {}

    const onUpdate = (e: Event) => {
      const ce = e as CustomEvent;
      setPayload(ce.detail as UpdatePayload);
    };

    const onStorage = (ev: StorageEvent) => {
      if (ev.key === 'app_update_payload' && ev.newValue) {
        try { setPayload(JSON.parse(ev.newValue)); } catch {}
      }
    };

    window.addEventListener('app_update_available', onUpdate as EventListener);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('app_update_available', onUpdate as EventListener);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  if (!payload) return null;

  const message = payload.message || `An update is available${payload.version ? ` (v${payload.version})` : ''}.`;
  const target = payload.redirectTo || '/';

  const handleRefresh = () => {
    window.location.href = target;
  };

  const handleDismiss = () => {
    setPayload(null);
  };

  return (
    <div className="w-full bg-yellow-50 border-b border-yellow-200 text-yellow-900">
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-start gap-3">
        <div className="mt-1">
          <svg className="w-5 h-5 text-yellow-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-.75-11.5a.75.75 0 011.5 0v5a.75.75 0 01-1.5 0v-5zm.75 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">Update available</span>
            {payload.version && <span className="text-xs px-2 py-0.5 rounded bg-yellow-100 border border-yellow-200">v{payload.version}</span>}
          </div>
          <div className="text-sm mt-1">{message}</div>
          {payload.releaseNotes && payload.releaseNotes.length > 0 && (
            <div className="mt-2">
              <button className="text-sm underline" onClick={() => setExpanded(!expanded)}>
                {expanded ? 'Hide details' : 'See what’s new'}
              </button>
              {expanded && (
                <ul className="mt-2 list-disc list-inside text-sm space-y-1">
                  {payload.releaseNotes.map((note, idx) => (
                    <li key={idx}>{note}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleRefresh} className="px-3 py-1.5 bg-secondary text-white rounded">Refresh now</button>
          <button onClick={handleDismiss} className="px-3 py-1.5 bg-white border border-yellow-300 text-yellow-800 rounded">Dismiss</button>
        </div>
      </div>
    </div>
  );
};

export default UpdateBanner;
